import axios from "axios";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { hostname, parseError } from "../utils";

interface NotificationContextType {
  unreadCount: number;
  refreshUnreadCount: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

interface NotificationProviderProps {
  children: ReactNode;
  companyId?: string;
  getToken: () => Promise<string>;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  companyId,
  getToken,
}) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // hostname is imported from utils

  const refreshUnreadCount = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (companyId) params.append("companyId", companyId);

      const response = await axios.get(
        `${hostname}/notifications/unread-count?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setUnreadCount(response.data.unreadCount);
    } catch (err) {
      setError(parseError(err));
      console.error("Failed to fetch unread count:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    refreshUnreadCount();
  }, [companyId]);

  const value: NotificationContextType = {
    unreadCount,
    refreshUnreadCount,
    isLoading,
    error,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
